/** User actions: loading images, ROI editing, spatial calibration helpers. */
import { batch } from '@preact/signals';
import { newRoiId, ROI_COLORS, translateRoi, type Roi, type RoiKind } from '../core/roi';
import type { RawFrame } from '../core/types';
import { decodeFile } from '../io/decodeClient';
import { busy, frames, notify, rawOptions, rois, selectedId, snapshotRois, spatial, type SlotId, SLOT_LABELS } from './store';

export async function loadBlob(slot: SlotId, blob: Blob, name: string, origin: RawFrame['meta']['origin'] = 'file') {
  busy.value = `Decoding ${name}…`;
  try {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const frame = await decodeFile(bytes, name, rawOptions.value);
    frame.meta.origin = origin;
    setFrame(slot, frame);
  } catch (e) {
    notify('error', `Could not open ${name}: ${(e as Error).message}`);
  } finally {
    busy.value = null;
  }
}

export function setFrame(slot: SlotId, frame: RawFrame | null) {
  frames[slot].value = frame;
  if (!frame) return;
  const kind = frame.isFloat ? 'float' : `${frame.meta.bitDepth}-bit`;
  notify(
    'success',
    `${SLOT_LABELS[slot]}: ${frame.meta.name} (${frame.width} × ${frame.height}, ${kind}, ${frame.channels === 1 ? 'grey' : 'RGB'})`,
  );
  for (const w of frame.meta.warnings) notify('warn', w);
  if (slot === 'A' && frame.meta.pixelsPerMm && spatial.value.unit === 'px') {
    const ppm = frame.meta.pixelsPerMm;
    notify('info', `The file declares a resolution of ${ppm.toPrecision(5)} px/mm.`, {
      action: { label: 'Use as spatial scale', run: () => (spatial.value = { unit: 'mm', perPixel: 1 / ppm }) },
      timeout: 15000,
    });
  }
}

export function clearSlot(slot: SlotId) {
  frames[slot].value = null;
}

// ------------------------------------------------------------------ ROIs

const NAMES: Record<RoiKind, string> = { line: 'Line', rect: 'Rect', ellipse: 'Ellipse', polygon: 'Polygon', point: 'Point' };

export function nextRoiDefaults(kind: RoiKind): { id: string; name: string; color: string; visible: boolean; locked: boolean } {
  const list = rois.value;
  const n = list.filter((r) => r.kind === kind).length + 1;
  let name = `${NAMES[kind]} ${n}`;
  let k = n;
  while (list.some((r) => r.name === name)) name = `${NAMES[kind]} ${++k}`;
  return { id: newRoiId(), name, color: ROI_COLORS[list.length % ROI_COLORS.length], visible: true, locked: false };
}

export function addRoi(r: Roi) {
  snapshotRois();
  batch(() => {
    rois.value = [...rois.value, r];
    selectedId.value = r.id;
  });
}

/** Replace a ROI. Pass record=true to create an undo step (e.g. at the start of a drag). */
export function updateRoi(id: string, next: Roi, record = false) {
  if (record) snapshotRois();
  rois.value = rois.value.map((r) => (r.id === id ? next : r));
}

export function patchRoi(id: string, patch: Partial<Roi>, record = true) {
  const r = rois.value.find((x) => x.id === id);
  if (!r) return;
  updateRoi(id, { ...r, ...patch } as Roi, record);
}

export function deleteRoi(id: string) {
  snapshotRois();
  batch(() => {
    rois.value = rois.value.filter((r) => r.id !== id);
    if (selectedId.value === id) selectedId.value = null;
  });
}

export function duplicateRoi(id: string) {
  const r = rois.value.find((x) => x.id === id);
  if (!r) return;
  const d = nextRoiDefaults(r.kind);
  addRoi({ ...translateRoi(r, 10, 10), id: d.id, name: `${r.name} copy`, color: d.color });
}

export function nudgeRoi(id: string, dx: number, dy: number, record: boolean) {
  const r = rois.value.find((x) => x.id === id);
  if (!r || r.locked) return;
  updateRoi(id, translateRoi(r, dx, dy), record);
}

export function clearRois() {
  if (!rois.value.length) return;
  snapshotRois();
  batch(() => {
    rois.value = [];
    selectedId.value = null;
  });
}
